/**
 * Payer-type settings (rules.payer_type_setting via /api/payer-type-settings): today,
 * each payer type's 0% touch age limit (MEASURE-RULES Rule 5.7, data/agedNoTouch.ts).
 *
 * Fetched once, after sign-in, in the background: the first paint never waits on it.
 * Until it arrives, and when it can't be read, every payer type reads as no limit, and
 * `status` says which so a page can say so.
 */
import { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { apiFetch } from '@/auth/apiFetch'
import { TOKEN_HEADER } from './client'
import { ageLimitsFrom, type AgeLimits } from './agedNoTouch'

export type AgeLimitsState =
  | { status: 'loading'; limits: AgeLimits }
  | { status: 'ok'; limits: AgeLimits }
  | { status: 'unavailable'; limits: AgeLimits; reason: string }

const NONE: AgeLimits = new Map()

/**
 * The limits as DatasetProvider fetched and applied them. Outside it (a test rendering
 * a page on its own) there are none, as before #695.
 */
export const AgeLimitsCtx = createContext<AgeLimitsState>({ status: 'ok', limits: NONE })

/** Each payer type's 0% touch age limit, as the figures on screen apply it. */
export const useAgeLimits = () => useContext(AgeLimitsCtx)

export function useFetchAgeLimits(): AgeLimitsState {
  const { token, ready } = useAuth()
  const [state, setState] = useState<AgeLimitsState>({ status: 'loading', limits: NONE })
  useEffect(() => {
    if (!ready) return
    let cancelled = false
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers[TOKEN_HEADER] = token
    apiFetch('/api/payer-type-settings', { headers })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Payer-type settings failed (${r.status}).`)
        const body = (await r.json()) as { settings?: { payerType: string; setting: string; value: number }[] }
        if (!cancelled) setState({ status: 'ok', limits: ageLimitsFrom(body.settings ?? []) })
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({ status: 'unavailable', limits: NONE, reason: e instanceof Error ? e.message : String(e) })
      })
    return () => {
      cancelled = true
    }
  }, [token, ready])
  return state
}
